import { FilterValues } from "@vms/SelectFilters/SelectFiltersTypes";
import { WordTypeEnum } from "@models/FilterValue";
import { getFormattedThousandWithDecimal } from "@core/helpers";

export enum FilterInputNameEnum {
    Segments = 'Segments',
    Regions = 'Regions',
    Cuisines = 'Cuisines',
    Restaurants = 'Restaurants',
    MinUnits = 'MinUnits',
    MaxUnits = 'MaxUnits',
    Appetizers = 'Appetizers',
    Entrees = 'Entrees',
    Sides = 'Sides',
    Desserts = 'Desserts',
    Beverages = 'Beverages',
    Words = 'Words',
    IncludeAllWords = 'Include ALL',
    IncludeAnyWords = 'Include ANY',
    ExcludeWords = 'Exclude Items',
    MinPrice = 'MinPrice',
    MaxPrice = 'MaxPrice',
    DayParts = 'Dayparts',
    EthnicSpeciality = 'Ethnic speciality',
    HealthyTerms = 'Healthy terms',
    SmallPlates = 'Small plates',
    KidMenus = 'Kid menus',
    GlutenFree = 'Gluten free',
    PlantBased = 'Plant based',
    Catering = 'Catering',
    Tags = 'Tags',
}

export type FilterChanges = {
    isSomeFieldChanged: boolean;
    changedFieldsCount: number;
    lastChangedFieldName: string | null;
    changes: Partial<Record<FilterInputNameEnum, string[]>>;
};

export const ALL_OPTIONS_SELECTED_VALUE = 'all';

export function getFiltersChanges(filters: FilterValues, currentCurrency: string): FilterChanges {
    let isSomeFieldChanged = false;
    let changedFieldsCount = 0;
    let lastChangedFieldName: string | null = null;
    const changes: FilterChanges['changes'] = {};

    const { venues, menuItems, attributes } = filters;

    if (!Object.keys(venues).length || !Object.keys(menuItems).length || !Object.keys(attributes).length) {
        return {
            isSomeFieldChanged,
            changedFieldsCount,
            lastChangedFieldName,
            changes,
        };
    }

    const {
        segments,
        hostedLocations,
        regions,
        cuisines,
        restaurants,
        minUnits,
        maxUnits,
    } = venues;

    const {
        appetizers,
        entrees,
        sides,
        desserts,
        beverages,
        words,
        minPrice,
        maxPrice,
    } = menuItems;

    const {
        dayParts,
        ethnicSpeciality,
        healthyTerms,
        smallPlates,
        kidMenus,
        glutenFree,
        plantBased,
        catering,
        tags,
    } = attributes;

    const getCheckedOptionsNames = (
        options: Array<{
            name?: string | null;
            isChecked: boolean;
        }>,
        putAllTogether: boolean = false,
    ): string[] | null => {
        if (options.length === 0) return null;

        const optionNames = options
            .filter(o => o.isChecked)
            .map(option => option.name ?? '');

        if (putAllTogether && optionNames.length === options.length) {
            return [ALL_OPTIONS_SELECTED_VALUE];
        }

        return optionNames;
    }

    const addCondition = (
        fieldName: FilterInputNameEnum,
        optionsNames: string[] | null,
    ) => {
        if (optionsNames !== null) {
            isSomeFieldChanged = isSomeFieldChanged || optionsNames.length > 0;
            if (optionsNames.length > 0) {
                changedFieldsCount++;
                lastChangedFieldName = fieldName;
                changes[fieldName] = optionsNames;
            }
        }
    };


    addCondition(FilterInputNameEnum.Segments, getCheckedOptionsNames(segments));
    addCondition(FilterInputNameEnum.Segments, getCheckedOptionsNames(hostedLocations));
    addCondition(FilterInputNameEnum.Regions, getCheckedOptionsNames(regions));
    addCondition(FilterInputNameEnum.Cuisines, getCheckedOptionsNames(cuisines));
    if (restaurants.length > 0) {
        addCondition(
            FilterInputNameEnum.Restaurants,
            restaurants.map(option => option.name || ''),
        );
    }
    if (minUnits !== null && minUnits !== 1) addCondition(
        FilterInputNameEnum.MinUnits,
        [`${minUnits}`]
    );
    if (maxUnits !== null) addCondition(
        FilterInputNameEnum.MaxUnits,
        [`${maxUnits}`],
    );

    // Menu items changes
    addCondition(FilterInputNameEnum.Appetizers, getCheckedOptionsNames(appetizers, true));
    addCondition(FilterInputNameEnum.Entrees, getCheckedOptionsNames(entrees, true));
    addCondition(FilterInputNameEnum.Sides, getCheckedOptionsNames(sides, true));
    addCondition(FilterInputNameEnum.Desserts, getCheckedOptionsNames(desserts, true));
    addCondition(FilterInputNameEnum.Beverages, getCheckedOptionsNames(beverages, true));
    if (words.length > 0) addCondition(
        FilterInputNameEnum.Words,
        words.map(option => option.name || ''),
    );
    const includeAllWordsOption = words.filter(
        ({ wordType }) => wordType === WordTypeEnum.IncludeAll
    );
    const includeAnyWordsOption = words.filter(
        ({ wordType }) => wordType === WordTypeEnum.IncludeAny
    );
    const excludeWordsOption = words.filter(
        ({ wordType }) => wordType === WordTypeEnum.Exclude
    );
    if (includeAllWordsOption.length > 0) {
        changes[FilterInputNameEnum.IncludeAllWords] = includeAllWordsOption.map(o => o.name || '');
    }
    if (includeAnyWordsOption.length > 0) {
        changes[FilterInputNameEnum.IncludeAnyWords] = includeAnyWordsOption.map(o => o.name || '');
    }
    if (excludeWordsOption.length > 0) {
        changes[FilterInputNameEnum.ExcludeWords] = excludeWordsOption.map(o => o.name || '');
    }

    if (minPrice !== null) addCondition(
        FilterInputNameEnum.MinPrice,
        [`${currentCurrency} ${getFormattedThousandWithDecimal(minPrice)}`]
    );
    if (maxPrice !== null) addCondition(
        FilterInputNameEnum.MaxPrice,
        [`${currentCurrency} ${getFormattedThousandWithDecimal(maxPrice)}`],
    );

    addCondition(FilterInputNameEnum.DayParts, getCheckedOptionsNames(dayParts));
    addCondition(FilterInputNameEnum.EthnicSpeciality, getCheckedOptionsNames(ethnicSpeciality));
    if (healthyTerms) addCondition(
        FilterInputNameEnum.HealthyTerms,
        [FilterInputNameEnum.HealthyTerms],
    );
    if (smallPlates) addCondition(
        FilterInputNameEnum.SmallPlates,
        [FilterInputNameEnum.SmallPlates],
    );
    if (kidMenus) addCondition(
        FilterInputNameEnum.KidMenus,
        [FilterInputNameEnum.KidMenus],
    );
    if (glutenFree) addCondition(
        FilterInputNameEnum.GlutenFree,
        [FilterInputNameEnum.GlutenFree],
    );
    if (plantBased) addCondition(
        FilterInputNameEnum.PlantBased,
        [FilterInputNameEnum.PlantBased],
    );
    if (catering !== false) {
        addCondition(
            FilterInputNameEnum.Catering,
            catering === true ? ['catering only'] : ['include catering']
        );
    }
    if (tags.length > 0)
        addCondition(
            FilterInputNameEnum.Tags,
            tags.map(tagId => `${tagId}`),
        );

    return {
        isSomeFieldChanged,
        changedFieldsCount,
        lastChangedFieldName,
        changes,
    };
}